import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search, Plus, Building2, SlidersHorizontal, Trash2, ChevronLeft, ChevronRight, Download, Loader2 } from 'lucide-react'
import { apiClient, clientsApi } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { Badge } from '@/components/common/Badge'
import { ErrorState } from '@/components/common/States'
import { confirm } from '@/lib/confirm'
import { cn } from '@/lib/utils'
import { exportToCsv } from '@/lib/exportCsv'
import { toast } from '@/lib/toast'
import { ClientForm } from '@/components/clients/ClientForm'
import type { Client, ClientStatus, PaginatedResponse } from '@/types'

const STATUS_VARIANT: Record<ClientStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  PROSPECT: 'info',
  ACTIVE: 'success',
  INACTIVE: 'warning',
  CHURNED: 'danger',
}

const STATUS_LABEL: Record<ClientStatus, string> = {
  PROSPECT: 'Prospect',
  ACTIVE: 'Attivo',
  INACTIVE: 'Inattivo',
  CHURNED: 'Perso',
}

const PAGE_LIMIT = 20

export function ClientsPage() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState<ClientStatus | ''>('')
  const [page, setPage] = useState(1)
  const [showForm, setShowForm] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [exporting, setExporting] = useState(false)

  const queryClient = useQueryClient()
  const canCreate = user?.isSuperAdmin || user?.role?.permissions?.clients?.includes('create') || false
  const canDelete = user?.isSuperAdmin || user?.role?.permissions?.clients?.includes('delete') || false

  const deleteMutation = useMutation({
    mutationFn: (id: string) => clientsApi.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] })
    },
  })

  const { data, isLoading, isError, refetch } = useQuery<PaginatedResponse<Client>>({
    queryKey: ['clients', { search, status: filterStatus, page }],
    queryFn: () =>
      apiClient.get<PaginatedResponse<Client>>('/clients', {
        search: search || undefined,
        status: filterStatus || undefined,
        page,
        limit: PAGE_LIMIT,
      }),
  })

  // Reset alla prima pagina quando cambiano i filtri di ricerca
  const resetPage = () => setPage(1)

  const clients = data?.items ?? data?.data ?? []
  const totalPages = data?.totalPages ?? data?.pagination?.totalPages ?? 1
  const total = data?.total ?? data?.pagination?.total ?? 0

  const handleDelete = async (e: React.MouseEvent, clientId: string, clientName: string) => {
    e.stopPropagation()
    if (
      await confirm({
        title: 'Elimina cliente',
        message: `Sei sicuro di voler eliminare "${clientName}"? Questa azione è irreversibile.`,
        danger: true,
      })
    ) {
      deleteMutation.mutate(clientId)
    }
  }

  const goToClient = (id: string) => navigate(`/clients/${id}`)

  async function handleExportCsv() {
    setExporting(true)
    try {
      const result = await apiClient.get<PaginatedResponse<Client>>('/clients', {
        search: search || undefined,
        status: filterStatus || undefined,
        page: 1,
        limit: 5000,
      })
      exportToCsv('clienti.csv', result.items ?? result.data ?? [], [
        { key: 'name', label: 'Nome', value: (c) => c.name },
        { key: 'status', label: 'Stato', value: (c) => STATUS_LABEL[c.status] ?? c.status },
        { key: 'tier', label: 'Tier', value: (c) => c.tier ?? '' },
        { key: 'email', label: 'Email', value: (c) => c.email ?? '' },
        { key: 'phone', label: 'Telefono', value: (c) => c.phone ?? '' },
        { key: 'vatNumber', label: 'P.IVA', value: (c) => c.vatNumber ?? '' },
        { key: 'totalInvoiced', label: 'Fatturato totale', value: (c) => c.totalInvoiced ?? '' },
        { key: 'createdAt', label: 'Data creazione', value: (c) => c.createdAt ? new Date(c.createdAt).toLocaleDateString('it-IT') : '' },
      ])
    } catch {
      toast.error('Esportazione fallita', 'Impossibile scaricare il CSV dei clienti.')
    } finally {
      setExporting(false)
    }
  }

  const GRID = canDelete
    ? 'grid-cols-[1fr_100px_100px_80px_50px]'
    : 'grid-cols-[1fr_100px_100px_80px]'

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Clienti</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Gestione del portfolio clienti (<span className="tabular-nums">{total}</span> totali)
          </p>
        </div>
        {canCreate && (
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nuovo Cliente
          </button>
        )}
      </div>

      {/* Search + filters */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Cerca clienti..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); resetPage() }}
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 placeholder:text-slate-400 transition-colors"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={cn(
            'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-lg transition-colors',
            showFilters || filterStatus
              ? 'bg-primary-50 border-primary-300 text-primary-700'
              : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
          )}
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filtri
          {filterStatus && (
            <span className="w-1.5 h-1.5 bg-primary-500 rounded-full" />
          )}
        </button>
        <button
          onClick={() => void handleExportCsv()}
          disabled={exporting}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-lg transition-colors bg-white border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Esporta CSV
        </button>
      </div>

      {/* Filter row */}
      {showFilters && (
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value as ClientStatus | ''); resetPage() }}
            className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 transition-colors"
          >
            <option value="">Tutti gli stati</option>
            {Object.entries(STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          {filterStatus && (
            <button
              onClick={() => { setFilterStatus(''); resetPage() }}
              className="text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
            >
              Rimuovi filtri
            </button>
          )}
        </div>
      )}

      {/* Error state */}
      {isError && !isLoading && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card">
          <ErrorState
            title="Impossibile caricare i clienti"
            description="Si è verificato un errore durante il caricamento del portfolio clienti."
            onRetry={() => void refetch()}
          />
        </div>
      )}

      {/* Mobile card list — visible below md */}
      <div className={cn('md:hidden space-y-2', isError && 'hidden')}>
        {isLoading && (
          <>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 shadow-card p-4 space-y-2">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-slate-100 rounded-lg animate-pulse flex-shrink-0" />
                  <div className="space-y-1.5 flex-1">
                    <div className="h-3.5 bg-slate-100 rounded animate-pulse w-3/4" />
                    <div className="h-2.5 bg-slate-100 rounded animate-pulse w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {!isLoading && clients.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-slate-200 shadow-card">
            <div className="p-3 bg-slate-100 rounded-full mb-4">
              <Building2 className="w-7 h-7 text-slate-400" />
            </div>
            <h3 className="text-base font-semibold text-slate-900 mb-1">
              {search || filterStatus ? 'Nessun cliente trovato' : 'Nessun cliente ancora'}
            </h3>
            <p className="text-sm text-slate-500">
              {search || filterStatus
                ? 'Prova a modificare i filtri di ricerca'
                : 'Aggiungi il primo cliente per iniziare'}
            </p>
          </div>
        )}

        {!isLoading && clients.map((client) => (
          <div
            key={client.id}
            role="button"
            tabIndex={0}
            aria-label={`Apri ${client.name}`}
            className="bg-white rounded-2xl border border-slate-200 shadow-card px-4 py-3 flex items-center gap-3 cursor-pointer hover:border-slate-300 active:bg-slate-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40"
            onClick={() => goToClient(client.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                goToClient(client.id)
              }
            }}
          >
            {/* Avatar */}
            <div className="w-9 h-9 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-semibold text-primary-700">{client.name.charAt(0)}</span>
            </div>

            {/* Main info */}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-900 truncate">{client.name}</p>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <Badge variant={STATUS_VARIANT[client.status]} dot size="sm">
                  {STATUS_LABEL[client.status]}
                </Badge>
                {client.tier && (
                  <span className="text-xs text-slate-400">{client.tier}</span>
                )}
                {client.totalInvoiced != null && (
                  <span className="text-xs text-slate-500 tabular-nums">
                    {new Intl.NumberFormat('it-IT', {
                      style: 'currency',
                      currency: 'EUR',
                      maximumFractionDigits: 0,
                    }).format(client.totalInvoiced)}
                  </span>
                )}
              </div>
            </div>

            {/* Delete */}
            {canDelete && (
              <button
                onClick={(e) => handleDelete(e, client.id, client.name)}
                disabled={deleteMutation.isPending}
                className="p-1.5 text-slate-400 hover:text-danger-600 hover:bg-danger-50 rounded-md transition-colors disabled:opacity-50 flex-shrink-0"
                title="Elimina cliente"
                aria-label={`Elimina ${client.name}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Desktop table — hidden on mobile */}
      <div className={cn('hidden md:block bg-white rounded-2xl border border-slate-200 shadow-card overflow-hidden', isError && 'md:hidden')}>
        {/* Table header */}
        <div className={cn('grid gap-4 px-4 py-2.5 border-b border-slate-200 bg-slate-50', GRID)}>
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Cliente</span>
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Stato</span>
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Note</span>
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Fatturato</span>
          {canDelete && <span />}
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="space-y-0 divide-y divide-slate-200">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={cn('grid gap-4 px-4 py-3', GRID)}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-slate-100 rounded-lg animate-pulse" />
                  <div className="space-y-1.5">
                    <div className="h-3 bg-slate-100 rounded animate-pulse w-32" />
                    <div className="h-2.5 bg-slate-100 rounded animate-pulse w-20" />
                  </div>
                </div>
                <div className="h-5 bg-slate-100 rounded-full animate-pulse w-16 self-center" />
                <div className="h-3 bg-slate-100 rounded animate-pulse w-20 self-center" />
                <div className="h-3 bg-slate-100 rounded animate-pulse w-16 self-center" />
                {canDelete && <div />}
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && clients.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="p-3 bg-slate-100 rounded-full mb-4">
              <Building2 className="w-7 h-7 text-slate-400" />
            </div>
            <h3 className="text-base font-semibold text-slate-900 mb-1">
              {search || filterStatus
                ? 'Nessun cliente trovato'
                : 'Nessun cliente ancora'}
            </h3>
            <p className="text-sm text-slate-500">
              {search || filterStatus
                ? 'Prova a modificare i filtri di ricerca'
                : 'Aggiungi il primo cliente per iniziare'}
            </p>
          </div>
        )}

        {/* Table rows */}
        {!isLoading && clients.length > 0 && (
          <div className="divide-y divide-slate-200">
            {clients.map((client) => (
              <div
                key={client.id}
                role="button"
                tabIndex={0}
                aria-label={`Apri ${client.name}`}
                className={cn('grid gap-4 px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500/40', GRID)}
                onClick={() => goToClient(client.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    goToClient(client.id)
                  }
                }}
              >
                {/* Name */}
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-semibold text-primary-700">
                      {client.name.charAt(0)}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{client.name}</p>
                    {client.address && (
                      <p className="text-xs text-slate-400 truncate">{client.address}</p>
                    )}
                  </div>
                </div>

                {/* Status */}
                <div>
                  <Badge variant={STATUS_VARIANT[client.status]} dot size="sm">
                    {STATUS_LABEL[client.status]}
                  </Badge>
                </div>

                {/* Notes */}
                <p className="text-xs text-slate-500 truncate">{client.notes ?? '—'}</p>

                {/* Fatturato */}
                <div>
                  <p className="text-sm font-medium text-slate-900 tabular-nums">
                    {client.totalInvoiced
                      ? new Intl.NumberFormat('it-IT', {
                          style: 'currency',
                          currency: 'EUR',
                          maximumFractionDigits: 0,
                        }).format(client.totalInvoiced)
                      : '—'}
                  </p>
                </div>

                {/* Delete */}
                {canDelete && (
                  <button
                    onClick={(e) => handleDelete(e, client.id, client.name)}
                    disabled={deleteMutation.isPending}
                    className="p-1.5 text-slate-400 hover:text-danger-600 hover:bg-danger-50 rounded-md transition-colors disabled:opacity-50"
                    title="Elimina cliente"
                    aria-label={`Elimina ${client.name}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && !isError && clients.length > 0 && totalPages > 1 && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-slate-500 tabular-nums">
            Pagina {page} di {totalPages} · {total} clienti totali
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              Precedente
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Successiva
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* New client modal */}
      {showForm && <ClientForm onClose={() => setShowForm(false)} />}
    </div>
  )
}
